import { Plus, Repeat1 } from "lucide-react";
import { useFormContext } from "react-hook-form";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
} from "../ui/dropdown-menu";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from "../ui/table";
import { RuleFormData } from "./builderSchema";
import { Type, Code } from "../../shared/schema";
import { useState } from "react";

interface PricingTableProps {
    dataType: Type[];
    attribute: Code[];
    setAttrCode: (code: string) => void;
    onClose: () => void;
    openExpression: boolean;
    setOpenExpression: (open: boolean) => void;
    setExpressionData: (data: any) => void;
}

export default function PricingTable({
    dataType,
    attribute,
    setAttrCode,
    onClose,
    openExpression,
    setOpenExpression,
    setExpressionData
}: PricingTableProps) {
    const { watch, setValue } = useFormContext<RuleFormData>();
    const pricingRows = watch("pricingRows");
    const ruleType = watch("ruleType");
    console.log("ruleType", ruleType)

    const [addWarning, setAddWarning] = useState("");

    const handlePricingChange = (rowId: number, key: string, value: any) => {
        setValue(
            "pricingRows",
            pricingRows.map((r) =>
                r.id === rowId ? { ...r, [key]: value?.Name ? value.Name : value } : r
            )
        );

        if (key === "dataType" || key === "dataType2") {
            setAttrCode(value?.ID);
        }
    };

    const handleAddPricingRow = (rowId: number) => {

        const currentRow = pricingRows.find((r) => r.id === rowId);
        if (!currentRow) return;

        const missingFields: string[] = [];

        if (!currentRow.dataType) missingFields.push("Data Type");
        else if (!currentRow.attribute) missingFields.push("Attribute");

        if (currentRow.expressionType === "value") {
            if (!currentRow.expressionValue) missingFields.push("Expression Value");
        } else {
            if (!currentRow.dataType2) missingFields.push("Operand Data Type");
            else if (!currentRow.attribute2) missingFields.push("Operand Attribute");
        }

        if (missingFields.length > 0) {
            setAddWarning(`Please fill: ${missingFields.join(", ")}`);
            setTimeout(() => setAddWarning(""), 2000);
            return;
        }


        const updated = pricingRows.map((r) =>
            r.id === rowId ? { ...r, isEditing: false, isNew: false } : r
        );
        setValue("pricingRows", [
            ...updated
        ]);
    };

    const handleEditPricingRow = (rowId: number) => {
        const currentlyEditingRow = pricingRows.find((r) => r.isEditing);

        if (currentlyEditingRow && currentlyEditingRow.id !== rowId) {
            const latestRow = pricingRows[pricingRows.length - 1];

            if (currentlyEditingRow.id === latestRow.id) {
                setValue(
                    "pricingRows",
                    pricingRows
                        .filter((r) => r.id !== latestRow.id)
                        .map((r) => ({
                            ...r,
                            isEditing: r.id === rowId
                        }))
                );
                return;
            }
        }

        setValue(
            "pricingRows",
            pricingRows.map((r) => ({
                ...r,
                isEditing: r.id === rowId
            }))
        );
    };

    const handleDeletePricingRow = (rowId: number) => {
        if (rowId > 1) {
            setValue("pricingRows", pricingRows.filter((r) => r.id !== rowId));
        } else {
            setValue("pricingRows", [
                {
                    id: 1,
                    isEditing: true,
                    dataType: "",
                    attribute: "",
                    operator: "=",
                    dataType2: "",
                    attribute2: "",
                    expressionType: "operand" as const,
                    expressionValue: "",
                    isNew: true
                }
            ]);
        }
    };

    const handleExpressionType = (rowId: number, type: "value" | "operand") => {
        setValue(
            "pricingRows",
            pricingRows.map((r) =>
                r.id === rowId
                    ? {
                        ...r,
                        expressionType: type,
                        dataType2: "",
                        attribute2: ""
                    }
                    : r
            )
        );
    };

    const NewRowPricing = () => {
        if (pricingRows.every((obj) => obj.isEditing === false)) {
            const nextId = Math.max(...pricingRows.map((r) => r.id)) + 1;
            setValue("pricingRows", [
                ...pricingRows,
                { id: nextId, isEditing: true, isNew: true, expressionType: "value" as const }
            ]);
            setAddWarning("")
        } else {
            setAddWarning("Please Add Rule Adjustment in the Row First")
            setTimeout(() => setAddWarning(""), 2000);
        }

    }

    return (
        <div className="border bg-white border-gray-200 rounded-lg p-2">
            <div className="flex items-center justify-between border-b pb-2 mb-1">
                <h2 className="text-lg font-semibold">{ruleType.label === "Form Rules" ? "Form" :"Pricing"} Rules Adjustment</h2>
                <div className="flex gap-2">
                    <Button
                        variant="default"
                        size="sm"
                        onClick={() => NewRowPricing()}
                        className="flex items-center gap-1"
                    >
                        <Plus className="h-4 w-4" />
                        {ruleType.label === "Form Rules" ? "Add Form Adjustment" :"Add Pricing Adjustment"}
                    </Button>
                    <Button variant="outline" size="sm" onClick={onClose}>
                        Close
                    </Button>
                </div>
            </div>

            <Table>
                <TableHeader>
                    <TableRow className="bg-gray-100">
                        <TableHead className="border p-2">Data Type</TableHead>
                        <TableHead className="border p-2">Attribute</TableHead>
                        <TableHead className="border p-2">Operator</TableHead>
                        <TableHead className="border p-2">Expression</TableHead>
                        <TableHead className="border p-2">Action</TableHead>
                    </TableRow>
                </TableHeader>

                <TableBody>
                    {pricingRows.map((row, idx) => (
                        <TableRow key={row.id}>
                            {row.isEditing ? (
                                <>
                                   
                                    <TableCell className="border p-1">
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant="outline" className="w-full text-left text-sm">
                                                    {dataType.length > 0 ? !row.dataType ? "Select" : row.dataType : "Fetching DataType..." }
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent>
                                                {dataType.map((opt) => (
                                                    <DropdownMenuItem
                                                        key={opt.Name}
                                                        onClick={() => handlePricingChange(row.id, "dataType", opt)}
                                                    >
                                                        {opt.Name}
                                                    </DropdownMenuItem>
                                                ))}
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </TableCell>

                                    
                                    <TableCell className="border p-1">
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant="outline" className="w-full text-left text-sm" disabled={!row.dataType || attribute.length === 0}>
                                                    {row.dataType ? attribute.length === 0 ? "No Attribute" : !row.attribute ? "Select" : row.attribute : "Select"}
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent>
                                                {attribute.map((opt) => (
                                                    <DropdownMenuItem
                                                        className="w-[250px]"
                                                        key={opt.Name}
                                                        onClick={() => handlePricingChange(row.id, "attribute", opt)}
                                                    >
                                                        {opt.Name}
                                                    </DropdownMenuItem>
                                                ))}
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </TableCell>

                                    
                                    <TableCell className="border p-1 text-center">=</TableCell>

                                    
                                    <TableCell className="border p-1">
                                        <div className="flex items-center gap-2">
                                            {row.expressionType === "value" ? (
                                                <Input
                                                    value={row.expressionValue || ""}
                                                    onChange={(e) =>
                                                        handlePricingChange(row.id, "expressionValue", e.target.value)
                                                    }
                                                    className="min-h-[40px] flex-1"
                                                />
                                            ) : (
                                                <>
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button variant="outline" className="w-full text-left text-sm">
                                                                {dataType.length > 0 ? !row.dataType2 ? "Select" : row.dataType2 : "Fetching DataType..." }
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent>
                                                            {dataType.map((opt) => (
                                                                <DropdownMenuItem
                                                                    key={opt.Name}
                                                                    onClick={() => handlePricingChange(row.id, "dataType2", opt)}
                                                                >
                                                                    {opt.Name}
                                                                </DropdownMenuItem>
                                                            ))}
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button variant="outline" className="w-full text-left text-sm" disabled={!row.dataType2 || attribute.length === 0}>
                                                                {row.dataType2 ? attribute.length === 0 ? "No Attribute" : !row.attribute2 ? "Select" : row.attribute2 : "Select"}
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent>
                                                            {attribute.map((opt) => (
                                                                <DropdownMenuItem
                                                                    className="w-[250px]"
                                                                    key={opt.Name}
                                                                    onClick={() => handlePricingChange(row.id, "attribute2", opt)}
                                                                >
                                                                    {opt.Name}
                                                                </DropdownMenuItem>
                                                            ))}
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </>
                                            )}

                                            
                                            <div className="flex gap-1">
                                                <Button
                                                    size="sm"
                                                    variant="link"
                                                    title={row.expressionType === "value" ? "Operand" : "Value"}
                                                    onClick={() =>
                                                        handleExpressionType(
                                                            row.id,
                                                            row.expressionType === "value" ? "operand" : "value"
                                                        )
                                                    }
                                                >
                                                    <Repeat1 className="h-4 w-4 text-gray-700" />
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="link"
                                                    title="Add More"
                                                    onClick={() => {
                                                        setOpenExpression(true);
                                                        handleExpressionType(row.id, "value");
                                                        setExpressionData(row);
                                                    }}
                                                >
                                                    <Plus className="h-4 w-4 text-gray-700" />
                                                </Button>
                                            </div>
                                        </div>
                                    </TableCell>

                                    
                                    <TableCell className="border p-1 flex gap-1">
                                        <Button size="sm" onClick={() => handleAddPricingRow(row.id)}>
                                            {row.isNew ? "Add & Continue" : "Update"}
                                        </Button>
                                        <Button size="sm" onClick={() => handleDeletePricingRow(row.id)}>
                                            {idx > 0 ? "Cancel" : "Clear"}
                                        </Button>
                                    </TableCell>
                                </>
                            ) : (
                                <>
                                    <TableCell className="border p-1 text-sm">{row.dataType}</TableCell>
                                    <TableCell className="border p-1 text-sm">{row.attribute}</TableCell>
                                    <TableCell className="border p-1 text-sm">=</TableCell>
                                    <TableCell className="border p-1 text-sm">
                                        {row.expressionValue
                                            ? row.expressionValue
                                            : `${row.dataType2}.${row.attribute2}`}
                                    </TableCell>
                                    <TableCell className="border p-1 text-sm">
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="mr-2"
                                            onClick={() => handleEditPricingRow(row.id)}
                                        >
                                            Edit
                                        </Button>
                                        {idx > 0 && (
                                            <Button
                                                size="sm"
                                                variant="destructive"
                                                onClick={() => handleDeletePricingRow(row.id)}
                                            >
                                                Delete
                                            </Button>
                                        )}
                                    </TableCell>
                                </>
                            )}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            {addWarning && (
                <div className="text-center text-red-600 mt-2 font-medium animate-fade">
                    {addWarning}
                </div>
            )}
        </div>
    );
}